import { FieldProps } from 'formik';
import { ReactElement, useMemo } from 'react';
import DeclarationField from 'views/declarations/common/DeclarationField';
import { getFormikProps } from 'views/declarations/utils/form-utils';
import { useTranslation } from 'react-i18next';
import FormSelect from '../../../../../components/ui/composed/declarations/formSelect/DeclarationSelect';
import { ValidationSchema, validators } from '../validations/validations';
import { AvailableForms } from '../../../../../hooks/useHideEntities';
import useCodelists from '../../../../../hooks/useCodelists';
import FormInput from '../../../../../components/ui/composed/declarations/formInput';
import useFormUtils from '../../../../../hooks/useFormUtils';

type Fields = 'id' | 'roleCode';

export const additionalFiscalReferencesBlockValidation: {
    childValidators: {
        'goodsShipment.domesticDutyTaxParty': ValidationSchema<Fields>;
    };
} = {
    childValidators: {
        'goodsShipment.domesticDutyTaxParty': {
            childValidators: {
                id: [validators.required(), validators.maxLength(17)],
                roleCode: [validators.required(), validators.exact(3)],
            },
            stopAtNull: true,
        },
    },
};

export const cdsImportAdditionalFiscalReferencesBlockValidation: {
    childValidators: {
        'goodsShipment.domesticDutyTaxParty': ValidationSchema<Fields>;
    };
} = {
    childValidators: {
        'goodsShipment.domesticDutyTaxParty': {
            childValidators: {
                id: [validators.required(), validators.maxLength(17)],
                roleCode: [validators.exact(3)],
            },
            stopAtNull: true,
        },
    },
};

export const additionalFiscalReferencesBlockProductValidation: {
    childValidators: {
        domesticDutyTaxParty: ValidationSchema<Fields>;
    };
} = {
    childValidators: {
        domesticDutyTaxParty: {
            childValidators: {
                id: [validators.required(), validators.maxLength(17)],
                roleCode: [validators.required(), validators.exact(3)],
            },
            stopAtNull: true,
        },
    },
};

interface Props extends BlockProps<Fields>, DeclarationBlockProps<AvailableForms, Fields> {}

const AdditionalFiscalReferencesBlock = ({
    path = 'goodsShipment.domesticDutyTaxParty',
    declarationOptions,
}: Props): ReactElement => {
    const { t } = useTranslation('form');
    const { formTypeForCds: formType } = useFormUtils();
    const { cdsCodelists } = useCodelists();

    const ucc = useMemo(() => '3/40', []);

    return (
        <>
            <DeclarationField pathPrefix={path} name="id">
                {({ form, field }: FieldProps<any>) => (
                    <FormInput
                        refNumber={ucc}
                        required
                        label={t(`${formType.includes('IMPORT') ? 'vatIdentificationNumber' : 'identifier'}`)}
                        {...getFormikProps(field.name, form)}
                        condensed
                    />
                )}
            </DeclarationField>
            <DeclarationField pathPrefix={path} name="roleCode">
                {({ form, field }: FieldProps<any>) => (
                    <FormSelect
                        refNumber={ucc}
                        required={declarationOptions?.[formType]?.fieldOptions?.roleCode?.required}
                        label={t(formType.includes('IMPORT') ? 'roleCode' : 'TypeCode')}
                        {...getFormikProps(field.name, form)}
                        selectOptions={cdsCodelists?.vatPartySubRoleTypes}
                        condensed
                        codeValidation={[validators.exact(3)]}
                    />
                )}
            </DeclarationField>
        </>
    );
};

export default AdditionalFiscalReferencesBlock;
